import { useEffect, useMemo, useState } from 'react'
import { ShieldCheck, X } from 'lucide-react'
import Button from './Button.js'
import Alert from './Alert.js'
import { shipmentService } from '../../services/shipmentService.js'

const DeliveryOtpModal = ({
  open,
  pending = [],
  onClose,
  onVerified,
}) => {
  const items = useMemo(
    () => (Array.isArray(pending) ? pending.filter((item) => item?.shipment_id) : []),
    [pending],
  )
  const [otpByShipment, setOtpByShipment] = useState({})
  const [verifyingId, setVerifyingId] = useState('')
  const [resendingId, setResendingId] = useState('')
  const [remaining, setRemaining] = useState(items)

  useEffect(() => {
    setRemaining(items)
    setOtpByShipment({})
  }, [items])

  if (!open || remaining.length === 0) return null

  const handleOtpChange = (shipmentId, value) => {
    const next = String(value || '').replace(/\D/g, '').slice(0, 6)
    setOtpByShipment((prev) => ({ ...prev, [shipmentId]: next }))
  }

  const markVerified = (shipmentId) => {
    const next = remaining.filter((item) => item.shipment_id !== shipmentId)
    setRemaining(next)
    if (next.length === 0) {
      onVerified?.()
    }
  }

  const handleVerify = async (item) => {
    const otp = otpByShipment[item.shipment_id] || ''
    if (otp.length !== 6) {
      Alert.error('Enter the 6-digit OTP sent on WhatsApp')
      return
    }
    try {
      setVerifyingId(item.shipment_id)
      const response = await shipmentService.verifyDeliveryOtp(item.shipment_id, otp)
      if (response.data?.success === false) {
        Alert.error(response.data?.error || 'Invalid OTP')
        return
      }
      Alert.success(`${item.shipment_id} delivered`)
      markVerified(item.shipment_id)
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to verify OTP')
    } finally {
      setVerifyingId('')
    }
  }

  const handleResend = async (item) => {
    try {
      setResendingId(item.shipment_id)
      const response = await shipmentService.resendDeliveryOtp(item.shipment_id)
      const nextItem = response.data?.pending?.[0]
      if (nextItem) {
        setRemaining((prev) =>
          prev.map((row) => (row.shipment_id === item.shipment_id ? { ...row, ...nextItem } : row)),
        )
      }
      Alert.success(response.data?.message || 'OTP resent on WhatsApp')
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to resend OTP')
    } finally {
      setResendingId('')
    }
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-orange-600" />
              Verify delivery OTP
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              OTP was sent on WhatsApp. Enter the code to auto-deliver each shipment.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {remaining.map((item) => {
            const phones = (item.masked_phones || []).filter(Boolean).join(', ')
            return (
              <div
                key={item.shipment_id}
                className="border border-orange-200 bg-orange-50/40 rounded-xl p-3"
              >
                <p className="text-sm font-semibold text-gray-900 break-all">{item.shipment_id}</p>
                {item.receiver_name && (
                  <p className="text-xs text-gray-600 mt-0.5">Receiver: {item.receiver_name}</p>
                )}
                <p className="text-xs text-gray-500 mt-0.5">
                  Sent to {phones || 'WhatsApp'}
                  {item.expires_in_seconds
                    ? ` · valid ${Math.max(1, Math.ceil(item.expires_in_seconds / 60))} min`
                    : ''}
                </p>
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otpByShipment[item.shipment_id] || ''}
                    onChange={(e) => handleOtpChange(item.shipment_id, e.target.value)}
                    placeholder="6-digit OTP"
                    maxLength={6}
                    className="flex-1 px-3 py-2 text-sm tracking-[0.3em] text-center border border-gray-300 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => handleVerify(item)}
                    loading={verifyingId === item.shipment_id}
                    disabled={verifyingId === item.shipment_id}
                  >
                    Verify & Deliver
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => handleResend(item)}
                  disabled={resendingId === item.shipment_id}
                  className="mt-2 text-xs text-orange-700 hover:underline disabled:opacity-50"
                >
                  {resendingId === item.shipment_id ? 'Resending...' : 'Resend OTP'}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default DeliveryOtpModal
